import React, { FC, useEffect, useMemo, useState } from 'react'
import { Alert, Flex, InputNumber, Modal, Segmented, Typography } from 'antd'
import { DirectivePromotionSuggestion } from '@pushly/models/lib/structs/pushly-ai/directive-promotion-suggestion'
import { getClassNames } from '../../_utils/classnames'
import {
    dimensionByBindingId,
    dimensionFor,
    promotionRationale,
    promotionSentence,
    type PromotionDimension,
} from './promotion-suggestions'
import { locateSpan } from './span-locator'

export interface PromotionTarget {
    /** The alternatives for ONE statement — a single entry, or one per field when the author's
     * sentence named a length but no field. */
    suggestions: DirectivePromotionSuggestion[]
    p90ByAttribute: Record<string, number>
    /** Medians, same keys. Context for how loose the p90 ceiling is — never a suggested value. */
    p50ByAttribute: Record<string, number>
    sampleSize: number
    /**
     * Limits the author has ALREADY written, keyed by binding attribute.
     *
     * The server never suggests a dimension that is already enforced, but this form lets the
     * author retarget onto a different field or unit — and that can land on one. Seen in a real
     * directive: "Write short sentences" is offered as a character limit, and switching the
     * measure to Words aims it at `title_max_words`, which their own sentence ("Use a maximum of
     * 6 words in the title.") already sets. Accepting would leave two sentences stating different
     * limits for the same thing, with the next compile deciding which one survives.
     */
    enforcedByAttribute: Record<string, unknown>
}

interface IPromotionModal {
    target: PromotionTarget | null
    /** The text as it stands right now — what the replacement will actually be spliced into. */
    body: string
    /** The exact text Analyze last compiled, so a span's offsets can be re-based onto `body`. */
    compiledBody?: string
    onCancel: () => void
    /**
     * The span to replace, the sentence to put there, and the rule that sentence expresses.
     *
     * The dimension is handed over rather than re-derived from the sentence: the caller uses it
     * to place the binding in the decomposition immediately, and parsing a number and a unit back
     * out of English to do that would be a second, weaker source of truth for something already
     * known here exactly.
     */
    onConfirm: (
        replaces: DirectivePromotionSuggestion['replaces'],
        sentence: string,
        dimension: { field: 'title' | 'body'; unit: 'characters' | 'words'; value: number },
    ) => void
}

/**
 * Confirms a promotion, and lets the author adjust it first (NS-2648).
 *
 * The suggestion is a proposal, not an answer. It is derived from what this domain has already
 * sent, which is a decent starting point and nothing more — the author may want the limit
 * tighter, or on the body instead of the title, or counted in words because that is how they
 * think about length. Applying straight from the row gave them one button and no way to say any
 * of that, and left the most consequential part of the whole feature (the exact number that
 * becomes a hard limit) as something to accept or ignore.
 *
 * A modal rather than a popover deliberately: this lives inside the Brand Voice drawer, which is
 * already a floating layer, and a popover on top of it would be a small form inside a panel
 * inside a drawer, clipped by the scrolling column it is anchored to.
 *
 * Confirming still only EDITS THE AUTHOR'S TEXT. Nothing is compiled or saved here — the rule
 * becomes real when they click Analyze, and the modal says so rather than implying the promotion
 * has already happened.
 */
export const PromotionModal: FC<IPromotionModal> = ({ target, body, compiledBody, onCancel, onConfirm }) => {
    // The first suggestion is the server's own lead proposal; for an ambiguous statement the
    // rest are its alternatives, all sharing one span.
    const lead = target?.suggestions[0]
    const leadDimension = lead ? dimensionByBindingId(lead.binding_id) : undefined

    const [field, setField] = useState<'title' | 'body'>(leadDimension?.field ?? 'title')
    const [unit, setUnit] = useState<'characters' | 'words'>(leadDimension?.unit ?? 'characters')
    const [value, setValue] = useState<number | null>(lead?.suggested_value ?? null)
    // Tracks whether the number on screen is still the one derived for the current dimension, so
    // switching field/unit can refresh it WITHOUT overwriting a number the author typed. Their
    // "40" means the same intent whether they then switch to words or not; silently replacing it
    // with a p90 would look like the form arguing with them.
    const [valueIsDerived, setValueIsDerived] = useState(true)

    // Reset whenever a different statement is opened — the modal is mounted once and reused, so
    // without this it would keep the previous suggestion's field, unit and number.
    useEffect(() => {
        if (!target) return
        const dimension = dimensionByBindingId(target.suggestions[0].binding_id)
        setField(dimension?.field ?? 'title')
        setUnit(dimension?.unit ?? 'characters')
        setValue(target.suggestions[0].suggested_value)
        setValueIsDerived(true)
    }, [target])

    const dimension: PromotionDimension = dimensionFor(field, unit)
    const derivedValue = target?.p90ByAttribute?.[dimension.attribute]

    // Re-derive the number when the author retargets, but only while they haven't set their own.
    useEffect(() => {
        if (valueIsDerived && derivedValue) setValue(derivedValue)
    }, [derivedValue, valueIsDerived])

    const rationale = target
        ? promotionRationale(dimension, target.p90ByAttribute, target.p50ByAttribute, target.sampleSize)
        : null
    const conflictsWith = target?.enforcedByAttribute?.[dimension.attribute]
    const sentence = value ? promotionSentence(dimension, value) : ''

    // Resolved against the CURRENT body, not the compiled one: the author may have kept typing
    // with the suggestion on screen. Doing it here means the reason is stated in the one place
    // they are looking, instead of a control quietly refusing to respond.
    const located = useMemo(
        () => (lead ? locateSpan(body, lead.replaces, compiledBody) : null),
        [lead, body, compiledBody],
    )
    const replacedText = located ? body.slice(located.start, located.end) : lead?.replaces.quote

    return (
        <Modal
            open={!!target}
            title="Make this a guarantee"
            okText="Update my text"
            okButtonProps={{ disabled: !located || !value || conflictsWith !== undefined }}
            onOk={() =>
                lead && value && onConfirm(lead.replaces, promotionSentence(dimension, value), { field, unit, value })
            }
            onCancel={onCancel}
            destroyOnHidden
            width={520}
        >
            {!!target && (
                <Flex vertical gap={16} className={getClassNames('bvd-promotion-modal')}>
                    {!located && (
                        <Alert
                            type="warning"
                            showIcon
                            message="This text has changed"
                            description="Click Analyze to re-read your Brand Voice, then try again — we can no longer tell which sentence this was about."
                        />
                    )}

                    {conflictsWith !== undefined && (
                        // Blocked rather than merged. Two sentences setting the same limit is not
                        // something to resolve on the author's behalf: the existing one is their
                        // own wording, still sitting in their text, and quietly overwriting it
                        // would leave the document contradicting itself.
                        <Alert
                            type="warning"
                            showIcon
                            message="You already set this limit"
                            description={`Your Brand Voice already limits ${dimension.field === 'title' ? 'titles' : 'bodies'} to ${String(conflictsWith)} ${dimension.unit}. Edit that sentence directly, or choose a different measure here.`}
                        />
                    )}

                    <div>
                        <Typography.Text type="secondary">Replacing</Typography.Text>
                        <Typography.Paragraph className={getClassNames('bvd-promotion-modal-quote')}>
                            “{replacedText}”
                        </Typography.Paragraph>
                    </div>

                    <Flex gap={16} wrap="wrap">
                        <Flex vertical gap={4}>
                            <Typography.Text type="secondary">Applies to</Typography.Text>
                            <Segmented
                                value={field}
                                onChange={(next) => {
                                    setField(next as 'title' | 'body')
                                    setValueIsDerived(true)
                                }}
                                options={[
                                    { label: 'Titles', value: 'title' },
                                    { label: 'Bodies', value: 'body' },
                                ]}
                            />
                        </Flex>
                        <Flex vertical gap={4}>
                            {/* The measure is a first-class choice, not a footnote. A limit of 62
                                characters and one of 62 words are entirely different rules, and
                                the author is committing to one of them. */}
                            <Typography.Text type="secondary">Measured in</Typography.Text>
                            <Segmented
                                value={unit}
                                onChange={(next) => {
                                    setUnit(next as 'characters' | 'words')
                                    setValueIsDerived(true)
                                }}
                                options={[
                                    { label: 'Characters', value: 'characters' },
                                    { label: 'Words', value: 'words' },
                                ]}
                            />
                        </Flex>
                        <Flex vertical gap={4}>
                            <Typography.Text type="secondary">No more than</Typography.Text>
                            <InputNumber
                                min={1}
                                value={value}
                                onChange={(next) => {
                                    setValue(next)
                                    setValueIsDerived(false)
                                }}
                                addonAfter={unit === 'words' ? 'words' : 'chars'}
                                style={{ width: 150 }}
                            />
                        </Flex>
                    </Flex>

                    {/* Where the number came from — or an honest blank. The author may retarget
                        onto a dimension the corpus never exercised, and inventing a reason for a
                        number nobody measured would be worse than admitting there isn't one. */}
                    <Typography.Text type="secondary" className={getClassNames('bvd-promotion-modal-rationale')}>
                        {rationale ??
                            'No reference for this measure — we have not measured enough of your sent content.'}
                    </Typography.Text>

                    <div>
                        <Typography.Text type="secondary">Your text will read</Typography.Text>
                        <Typography.Paragraph className={getClassNames('bvd-promotion-modal-preview')}>
                            {sentence || '—'}
                        </Typography.Paragraph>
                    </div>

                    {/* Sets the expectation the button cannot: this edits the text and stops.
                        Without it, "Make this a guarantee" reads as though the rule is now live. */}
                    <Typography.Text type="secondary" className={getClassNames('bvd-promotion-modal-note')}>
                        This updates your Brand Voice text. Click Analyze to see it move to Guaranteed.
                    </Typography.Text>
                </Flex>
            )}
        </Modal>
    )
}
